// 一覧 tab — filters, IP table with selection, unplaced devices.
// Columns are user-configurable via 列設定 (per-browser localStorage).
import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { MagnifyingGlass, PencilSimple, UserPlus, WarningCircle, SlidersHorizontal, Lightning } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PURPOSES, purposeColor } from '../purposes';
import { itApi } from '../itApi';
import { ipListCols } from './ipListColumns';
import { useTableColumns } from '@/lib/useTableColumns';
import TableColumnsDialog from '@/components/table/TableColumnsDialog';
import OmadaWriteDialog from './OmadaWriteDialog';

export const IpListTab = ({ allEntries, loading, unplaced, selected, setSelected, onEdit, onReserve }) => {
  const [search, setSearch] = useState('');
  const [connFilter, setConnFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [authFreeFilter, setAuthFreeFilter] = useState('all');
  const [purposeFilter, setPurposeFilter] = useState('all');
  const [colsDialogOpen, setColsDialogOpen] = useState(false);
  const [omadaWriteEntry, setOmadaWriteEntry] = useState(null);
  const cols = useTableColumns(ipListCols);

  const filtered = useMemo(() => allEntries.filter((e) => {
    if (connFilter !== 'all' && e.connection !== connFilter) return false;
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (authFreeFilter === 'yes' && !e.auth_free) return false;
    if (purposeFilter === 'none' && e.purpose) return false;
    if (purposeFilter !== 'all' && purposeFilter !== 'none' && (e.purpose || '') !== purposeFilter) return false;
    if (search) {
      const hay = [e.ip_address, e.user, e.device_name, e.mac_address, e.purpose, e.note]
        .map((x) => (x || '').toLowerCase()).join(' ');
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  }), [allEntries, connFilter, statusFilter, authFreeFilter, purposeFilter, search]);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allFilteredSelected = filtered.length > 0 && filtered.every((e) => selected.has(e.id));
  const toggleSelAll = () => setSelected(() => (allFilteredSelected ? new Set() : new Set(filtered.map((e) => e.id))));

  // Bulk fixed-IP — pins every selected Omada row to its current IP in one batch.
  const [bulkBusy, setBulkBusy] = useState(false);
  const selectedOmadaRows = useMemo(() => allEntries.filter((e) => selected.has(e.id) && e.source === 'omada' && e.mac_address && e.ip_address), [allEntries, selected]);
  const bulkFixedIp = async () => {
    if (selectedOmadaRows.length === 0) return;
    if (!window.confirm(`選択中の ${selectedOmadaRows.length} 件の Omada 端末を、それぞれ現在のIPで固定化しますか？\nOmadaへの反映まで最大10分かかります。`)) return;
    setBulkBusy(true);
    let ok = 0; const fails = [];
    // 逐次実行（1件ずつ）— 254件でも数秒で完了、キューの順序も保たれる
    for (const e of selectedOmadaRows) {
      try {
        await itApi.omadaWrite(e.id, { fixed_ip: e.ip_address });
        ok += 1;
      } catch (err) {
        fails.push(`${e.ip_address}: ${err?.response?.data?.detail || err.message}`);
      }
    }
    setBulkBusy(false);
    if (ok > 0) toast.success(`${ok} 件を固定IP設定に予約しました`, { description: 'ルーターの同期で最大10分後に反映' });
    if (fails.length > 0) toast.error(`${fails.length} 件で失敗`, { description: fails.slice(0, 3).join(' / ') });
  };

  // Column definitions — key must match ipListColumns.js. Each returns
  // { header: JSX, cell: (e) => JSX, thCls, tdCls }.
  const COLUMN_MAP = {
    ip_address: {
      header: 'IPアドレス',
      cell: (e) => <span className="font-mono text-zinc-800">{e.ip_address}</span>,
    },
    status: {
      header: '状態',
      cell: (e) => (
        <div className="flex flex-wrap items-center gap-1">
          {e.status === 'assigned'
            ? <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">割当済み</Badge>
            : <Badge variant="outline" className="text-zinc-500">空き</Badge>}
          {e.auth_free && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100" data-testid={`auth-free-badge-${e.no}`}>認証フリー</Badge>}
          {e.source === 'omada' && <Badge variant="outline" className="text-[10px] text-blue-500 border-blue-200">Omada</Badge>}
          {e.protected_reason && (
            <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-200"
              title={`${e.protected_reason} — 自動解放されません`}
              data-testid={`protected-badge-${e.no}`}>
              {e.is_network_device ? 'ネットワーク機器' : '固定'}
            </Badge>
          )}
          {e.stale && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100 text-[10px]"
              title={`最終確認: ${(e.last_synced || '').slice(0, 10)} — 資産に紐付いているため自動解放されません`}
              data-testid={`stale-badge-${e.no}`}>
              30日+未確認
            </Badge>
          )}
        </div>
      ),
    },
    device_name: { header: 'デバイス名', cell: (e) => <span className="text-zinc-600">{e.device_name || '—'}</span> },
    user: { header: '使用者', cell: (e) => <span className="text-zinc-800">{e.user || '—'}</span> },
    mac_address: { header: 'MACアドレス', cell: (e) => <span className="font-mono text-xs text-zinc-500">{e.mac_address || '—'}</span> },
    purpose: {
      header: '用途',
      cell: (e) => e.purpose ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-zinc-700" data-testid={`purpose-badge-${e.no}`}>
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: purposeColor(e.purpose) }} />
          {e.purpose}
        </span>
      ) : <span className="text-zinc-300">—</span>,
    },
  };
  const visibleCols = cols.applyTo(Object.fromEntries(Object.entries(COLUMN_MAP).map(([k, v]) => [k, { key: k, ...v }])));
  const totalColCount = visibleCols.length + 2; // + select + actions

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center" data-testid="ip-list-filters">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="ip-search" placeholder="IP・使用者・デバイス・MACで検索" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-11 rounded-lg border-slate-300" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="filter-status" className="w-36 h-11 rounded-lg border-slate-300"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">状態: すべて</SelectItem>
            <SelectItem value="assigned">割当済み</SelectItem>
            <SelectItem value="available">空き</SelectItem>
          </SelectContent>
        </Select>
        <Select value={connFilter} onValueChange={setConnFilter}>
          <SelectTrigger data-testid="filter-connection" className="w-32 h-11 rounded-lg border-slate-300"><SelectValue placeholder="接続" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">接続: すべて</SelectItem>
            <SelectItem value="有線">有線</SelectItem>
            <SelectItem value="無線">無線</SelectItem>
          </SelectContent>
        </Select>
        <Select value={purposeFilter} onValueChange={setPurposeFilter}>
          <SelectTrigger data-testid="filter-purpose" className="w-44 h-11 rounded-lg border-slate-300"><SelectValue placeholder="用途" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">用途: すべて</SelectItem>
            <SelectItem value="none">未設定</SelectItem>
            {PURPOSES.map((p) => <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={authFreeFilter} onValueChange={setAuthFreeFilter}>
          <SelectTrigger data-testid="filter-auth-free" className="w-40 h-11 rounded-lg border-slate-300"><SelectValue placeholder="認証フリー" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">認証フリー: すべて</SelectItem>
            <SelectItem value="yes">認証フリーのみ</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="reserve-next-btn" onClick={onReserve} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800">
          <UserPlus size={16} className="mr-1.5" /> 次の空きIPを予約
        </Button>
        {selectedOmadaRows.length > 0 && (
          <Button type="button" onClick={bulkFixedIp} disabled={bulkBusy}
            data-testid="bulk-fixed-ip-btn"
            className="h-11 rounded-lg bg-amber-500 hover:bg-amber-600 text-white">
            <Lightning size={16} weight="fill" className="mr-1.5" />
            選択 {selectedOmadaRows.length} 件を固定IPに
          </Button>
        )}
        <Button type="button" variant="outline" onClick={() => setColsDialogOpen(true)}
          data-testid="ip-cols-btn" title="列の表示・並び替え"
          className="h-11 rounded-lg border-slate-300 text-zinc-600 hover:text-zinc-800">
          <SlidersHorizontal size={14} weight="bold" className="mr-1.5" /> 列設定
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm" data-testid="ip-data-table">
            <thead>
              <tr className="bg-slate-50 text-zinc-500 text-xs">
                <th className="px-3 py-3 w-10"><Checkbox checked={allFilteredSelected} onCheckedChange={toggleSelAll} data-testid="select-all-ips" /></th>
                {visibleCols.map((c) => (
                  <th key={c.key} className="text-left font-semibold px-4 py-3 whitespace-nowrap">{c.header}</th>
                ))}
                <th className="text-right font-semibold px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={totalColCount} className="px-4 py-10 text-center text-zinc-400">読み込み中...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={totalColCount} className="px-4 py-10 text-center text-zinc-400">該当するIPがありません</td></tr>
              ) : filtered.map((e) => (
                <tr key={e.id} className={`border-t border-slate-50 hover:bg-slate-50/60 ${selected.has(e.id) ? 'bg-blue-50/50' : ''}`} data-testid={`ip-row-${e.no}`}>
                  <td className="px-3 py-2.5"><Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggleSel(e.id)} data-testid={`select-ip-${e.no}`} /></td>
                  {visibleCols.map((c) => (
                    <td key={c.key} className="px-4 py-2.5">{c.cell(e)}</td>
                  ))}
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {e.source === 'omada' && e.mac_address && (
                      <Button size="sm" variant="ghost" data-testid={`omada-write-${e.no}`} onClick={() => setOmadaWriteEntry(e)}
                        className="h-8 px-2 text-amber-600 hover:bg-amber-50" title="Omadaへ書き戻し（リネーム・固定IP・解放）">
                        <Lightning size={16} weight="fill" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`edit-ip-${e.no}`} onClick={() => onEdit(e)} className="h-8 px-2 text-zinc-600"><PencilSimple size={16} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Unplaced (offline) owners */}
      {unplaced.length > 0 && (
        <div className="bg-amber-50/60 rounded-xl border border-amber-200 p-4" data-testid="unplaced-section">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><WarningCircle size={16} /> 未配置（オフライン）の端末 {unplaced.length}件</p>
          <p className="text-[11px] text-amber-700 mt-0.5 mb-2">台帳に登録済みですが、現在Omadaで検出されていません。次回そのMACが同期された時点で自動的に正しいVLANへ配置されます。</p>
          <div className="flex flex-wrap gap-2">
            {unplaced.slice(0, 40).map((u) => (
              <span key={u.mac} className="inline-flex items-center gap-1.5 bg-white border border-amber-200 rounded-md px-2 py-1 text-xs text-zinc-700">
                <b>{u.device_name || u.user || u.mac}</b>
                {u.user && u.device_name && <span className="text-zinc-400">{u.user}</span>}
                <span className="font-mono text-[10px] text-zinc-400">{u.mac}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <TableColumnsDialog open={colsDialogOpen} onClose={() => setColsDialogOpen(false)}
        config={cols.config} onToggle={cols.toggle} onMove={cols.move} onReset={cols.reset}
        helpers={ipListCols} title="IP一覧の列" testidPrefix="ip-col" />

      <OmadaWriteDialog open={!!omadaWriteEntry} entry={omadaWriteEntry}
        onClose={() => setOmadaWriteEntry(null)}
        onDone={() => setOmadaWriteEntry(null)} />
    </div>
  );
};
